import { Injectable } from '@angular/core';

import { setSessionData } from '@hub-store/session/session.actions';
import { getSession } from '@hub-store/session/session.selectors';
import { HubSessionState } from '@hub-store/session/session.reducer';
import { Store } from '@ngrx/store';
import { catchError, combineLatest, Observable, of, switchMap, take, tap } from 'rxjs';
import { SessionsService } from '@hub-services/sessions.service';
import { PartnersService } from '@hub-services/partners.service';
import { Partner } from '@hub-services/partner';
import * as SelectedPartnerActions from '@hub-store/selected-partner/selected-partner.actions';
import * as UaConfigActions from '@hub-store/ua-config/ua-config.actions';
import { getSelectedPartner } from '@hub-store/selected-partner/selected-partner.selectors';
import { AuthService } from '../../auth/auth.service';

@Injectable({ providedIn: 'root' })
export class SessionResolve  {
  constructor(
    private sessionsService: SessionsService,
    private partnersService: PartnersService,
    private store: Store,
    private authService: AuthService) { }

  resolve() {

    return combineLatest([this.store.select(getSession),this.store.select(getSelectedPartner)])
      .pipe(
        take(1),
        switchMap(([session, partner]) => {
          return session?.user_id
            ? this.hydratePartnerFromSession(session, partner)
            : this.sessionsService
              .getSession()
              .pipe(
                switchMap((sessionData: any) => {
                  if (!sessionData) {
                    return this.authService.handleSessionUnavailable();
                  }

                  this.store.dispatch(setSessionData({ data: sessionData }));
                  return this.hydratePartnerFromSession(sessionData, partner);
                }),
                catchError((err) => {
                  console.error('SessionResolve: session fetch error', err?.status);
                  return this.authService.handleSessionUnavailable();
                })
              );
        }));

  }

  private hydratePartnerFromSession(
    sessionData: HubSessionState,
    partner: Partner | null,
  ): Observable<HubSessionState> {
    if (!sessionData?.partner_id || !sessionData?.partner_type) {
      return of(sessionData);
    }

    if (sessionData.partner_id === partner?.id && !partner?.partialSeed) {
      return of(sessionData);
    }

    return this.partnersService
      .getPartner(sessionData.partner_type, sessionData.partner_id)
      .pipe(
        take(1),
        tap((nextPartner: Partner) => {
          this.store.dispatch(SelectedPartnerActions.setPartner({ partner: nextPartner }));
          this.store.dispatch(UaConfigActions.setLanguageCode({ languageCode: nextPartner.default_language_code ?? 'en' }));
        }),
        switchMap(() => of(sessionData))
      );
  }
}
